import React from 'react';
import { skills, training } from '../mock/komalData';
import { Award, Film, Languages, Swords, GraduationCap } from 'lucide-react';

const SkillsTraining = () => {
  return (
    <section id="skills" className="bg-[#1a1a1a] py-16 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Training Section - Compact */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className="font-display text-4xl sm:text-5xl font-black text-white mb-3">
              TRAINING & <span className="text-[#FFD700]">CREDENTIALS</span>
            </h2>
            <div className="w-16 h-1 bg-[#FFD700] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {training.map((item, index) => (
              <div
                key={index}
                className="bg-[#0a0a0a] p-5 rounded-lg border border-gray-800 hover:border-[#FFD700] transition-all"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#FFD700]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="text-[#FFD700]" size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white">{item.institution}</h3>
                    <p className="text-[#FFD700] text-sm font-medium">{item.course}</p>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mb-2">{item.description}</p>
                <div className="flex items-center justify-between text-xs">
                  <p className="text-[#4ECDC4] font-medium">
                    <span className="text-gray-500">Mentor:</span> {item.mentor}
                  </p>
                  <p className="text-gray-500">{item.year}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Section - Compact */}
        <div>
          <div className="text-center mb-8">
            <h2 className="font-display text-4xl sm:text-5xl font-black text-white mb-3">
              SKILLS & <span className="text-[#4ECDC4]">ABILITIES</span>
            </h2>
            <div className="w-16 h-1 bg-[#4ECDC4] mx-auto"></div>
          </div>

          {/* Compact 3-Column Grid */}
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Combat & Physical Training */}
            <div className="bg-[#0a0a0a] p-5 rounded-lg border border-gray-800 hover:border-[#4ECDC4]/50 transition-all">
              <div className="flex items-center gap-2 mb-4">
                <Swords className="text-[#4ECDC4]" size={20} />
                <h3 className="text-lg font-bold text-white">Combat & Physical</h3>
              </div>
              <div className="space-y-2">
                {skills.combatPhysical.map((skill, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-300">{skill.name}</span>
                    <span className="text-[#4ECDC4] text-xs font-medium">{skill.level}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Performing Arts */}
            <div className="bg-[#0a0a0a] p-5 rounded-lg border border-gray-800 hover:border-[#FF6B6B]/50 transition-all">
              <div className="flex items-center gap-2 mb-4">
                <Film className="text-[#FF6B6B]" size={20} />
                <h3 className="text-lg font-bold text-white">Performing Arts</h3>
              </div>
              <div className="space-y-2">
                {skills.performingArts.map((skill, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-300">{skill.name}</span>
                    <span className="text-[#FF6B6B] text-xs font-medium">{skill.level}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Languages - Compact */}
            <div className="bg-[#0a0a0a] p-5 rounded-lg border border-gray-800 hover:border-[#FFD700]/50 transition-all">
              <div className="flex items-center gap-2 mb-4">
                <Languages className="text-[#FFD700]" size={20} />
                <h3 className="text-lg font-bold text-white">Languages</h3>
              </div>
              
              {/* Fluent */}
              <div className="mb-3">
                <h4 className="text-[#FFD700] text-xs font-semibold mb-2 uppercase">Fluent</h4>
                <div className="flex flex-wrap gap-1.5">
                  {skills.languages.fluent.map((lang, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 bg-[#FFD700]/10 border border-[#FFD700]/50 text-[#FFD700] rounded-full text-xs font-medium"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>

              {/* Proficient */}
              <div>
                <h4 className="text-gray-500 text-xs font-semibold mb-2 uppercase">Proficient</h4>
                <div className="flex flex-wrap gap-1.5">
                  {skills.languages.proficient.map((lang, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 bg-gray-800 text-gray-400 rounded-full text-xs"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Compact Unique Edge */}
          <div className="mt-10 max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-[#FF6B6B]/10 via-[#4ECDC4]/10 to-[#FFD700]/10 p-6 rounded-lg border border-gray-800">
              <div className="flex items-start gap-4">
                <Award className="text-[#FF6B6B] flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">My Unique Edge</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    A rare combination of performing arts, martial arts, and multilingual fluency. Growing up across 
                    different states in India has given me cultural versatility and the ability to authentically portray 
                    diverse characters. From action-packed roles to emotionally layered performances. I bring a unique approach to every character.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsTraining;
